import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Row, Col, Label, Input, FormGroup, Card, CardBody } from 'reactstrap';
import { getCompanyById, getEstablishment, getEmissionPoint } from '../../../slices/companies/api';

// Utilidad local para decodificar el payload del JWT sin dependencias externas
const decodeJwtPayload = (token?: string | null): any | null => {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (_e) {
        return null;
    }
};

const IssuerInfo = ({ formik, selectedCompany, selectedEstablishment, selectedEmissionPoint, onCompanyChange }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => setIsOpen(!isOpen);

    // Estados locales derivados del token cuando no llegan por props
    const [company, setCompany] = useState<any>(null);
    const [establishment, setEstablishment] = useState<any>(null);
    const [emissionPoint, setEmissionPoint] = useState<any>(null);

    const effectiveCompany = selectedCompany ?? company;
    const effectiveEstablishment = selectedEstablishment ?? establishment;
    const effectiveEmissionPoint = selectedEmissionPoint ?? emissionPoint;

    // Decodificar el token una sola vez
    const tokenPayload = useMemo(() => {
        const authUserRaw = localStorage.getItem('authUser');
        try {
            const parsed = authUserRaw ? JSON.parse(authUserRaw) : null;
            return decodeJwtPayload(parsed?.token);
        } catch {
            return null;
        }
    }, []);

    // Detectar si es Super Admin
    const isSuperAdmin = tokenPayload?.roles?.some((role: any) => role.name === 'Super Admin') || false;

    // Cargar datos de compañía, establecimiento y punto de emisión si no se recibieron por props
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!tokenPayload) return;

            try {
                // Cargar compañía desde el token si no hay una seleccionada (para todos los roles)
                if (!selectedCompany && tokenPayload.companyId) {
                    const resp: any = await getCompanyById(tokenPayload.companyId);
                    const loadedCompany = resp?.data ?? resp;
                    if (!cancelled) setCompany(loadedCompany);
                    
                    // 🔄 Notificar al componente padre (importante para Super Admin)
                    if (onCompanyChange && loadedCompany) {
                        onCompanyChange(loadedCompany);
                    }
                }
            } catch {}

            try {
                if (!selectedEstablishment && tokenPayload.establishmentId && !isSuperAdmin) {
                    const resp: any = await getEstablishment(tokenPayload.establishmentId);
                    if (!cancelled) setEstablishment(resp?.data ?? resp);
                }
            } catch {}

            try {
                if (!selectedEmissionPoint && tokenPayload.emissionPointId && !isSuperAdmin) {
                    const resp: any = await getEmissionPoint(tokenPayload.emissionPointId);
                    if (!cancelled) setEmissionPoint(resp?.data ?? resp);
                }
            } catch {}
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [selectedCompany, selectedEstablishment, selectedEmissionPoint, tokenPayload, onCompanyChange, isSuperAdmin]);

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3 mb-4">
            <div className="bg-soft-primary p-3 border-bottom border-light d-flex justify-content-between align-items-center" onClick={toggle} style={{ cursor: 'pointer' }}>
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-store-2-fill me-2 fs-18"></i>
                    Datos del Emisor
                </h5>
                <i className={isOpen ? "ri-arrow-up-s-line fs-4 text-primary" : "ri-arrow-down-s-line fs-4 text-primary"}></i>
            </div>

            <Collapse isOpen={isOpen}>
                <CardBody className="p-4 bg-white">
                    <Row>
                    <Col md={6} className="mb-3">
                        <Label className="form-label text-muted">Razón Social</Label>
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0"><i className="ri-building-line text-muted"></i></span>
                            <Input type="text" value={effectiveCompany?.businessName || ''} disabled className="bg-light border-start-0 ps-0 fw-medium" />
                        </div>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label className="form-label text-muted">Nombre Comercial</Label>
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0"><i className="ri-store-3-line text-muted"></i></span>
                            <Input value={effectiveCompany?.tradeName || ''} disabled className="bg-light border-start-0 ps-0 fw-medium" />
                        </div>
                    </Col>
                    <Col md={3} className="mb-3">
                        <Label className="form-label text-muted">RUC</Label>
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0"><i className="ri-profile-line text-muted"></i></span>
                            <Input value={effectiveCompany?.ruc || ''} disabled className="bg-light border-start-0 ps-0" />
                        </div>
                    </Col>
                    <Col md={9} className="mb-3">
                        <Label className="form-label text-muted">Dirección Matriz</Label>
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0"><i className="ri-map-pin-line text-muted"></i></span>
                            <Input value={effectiveCompany?.address || ''} disabled className="bg-light border-start-0 ps-0" />
                        </div>
                    </Col>
                    <Col md={3}>
                        <Label className="form-label text-muted">Establecimiento</Label>
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0"><i className="ri-store-line text-muted"></i></span>
                            <Input type="text" value={effectiveEstablishment ? effectiveEstablishment.code : ''} disabled className="bg-light border-start-0 ps-0 text-center" />
                        </div>
                    </Col>
                    <Col md={3}>
                        <Label className="form-label text-muted">Punto de Emisión</Label>
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0"><i className="ri-printer-line text-muted"></i></span>
                            <Input type="text" value={effectiveEmissionPoint ? effectiveEmissionPoint.code : ''} disabled className="bg-light border-start-0 ps-0 text-center" />
                        </div>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label fw-bold">Fecha de Emisión <span className="text-danger">*</span></Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0"><i className="ri-calendar-line text-muted"></i></span>
                                <Input
                                    type="date"
                                    name="issueDate"
                                    value={formik.values.issueDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.issueDate && formik.errors.issueDate)}
                                    disabled
                                    className="bg-light border-start-0 ps-0"
                                />
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Guía de Remisión</Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0"><i className="ri-file-text-line text-muted"></i></span>
                                <Input name="remissionGuide" value={formik.values.remissionGuide} onChange={formik.handleChange} placeholder="Opcional" className="border-start-0 ps-0" />
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup check className="mt-2">
                            <Input type="checkbox" name="negotiableCommercialInvoice" checked={formik.values.negotiableCommercialInvoice} onChange={formik.handleChange} id="negotiableCheck" />
                            <Label check htmlFor="negotiableCheck" className="text-muted">Factura Comercial Negociable</Label>
                        </FormGroup>
                    </Col>
                </Row>
                </CardBody>
            </Collapse>
        </Card>
    );
};

export default IssuerInfo;
